import { history, useModel } from '@umijs/max';
import { useTenant } from '@zhiyue/core';
import { BizAppConfig } from 'config/application.manifest';
import React from 'react';
import { buildAuthorizedResourceAccessMap } from '@/permissions/adapter';
import { getMyAuthorizedResources } from '@/services/bff/permission.api';
import { hasOAuth2Session } from '@/services/oauth2';

export default function PermissionSync() {
  const { initialState, setInitialState } = useModel('@@initialState');
  const previousAccess = React.useRef(
    initialState?.authorizedResourceAccess ?? {},
  );
  const { currentTenantUuid } = useTenant();
  React.useEffect(() => {
    let active = true;
    let revision = 0;
    const apply = async (access: Record<string, boolean>) => {
      const revoked = Object.entries(previousAccess.current).some(
        ([key, allowed]) => allowed && !access[key],
      );
      previousAccess.current = access;
      await setInitialState((prev) => ({
        ...prev,
        authorizedResourceAccess: access,
      }));
      if (
        active &&
        revoked &&
        /^\/(admin|employee)(\/|$)/.test(history.location.pathname)
      )
        history.replace('/');
    };
    const refresh = async () => {
      if (!hasOAuth2Session()) return;
      const currentRevision = ++revision;
      try {
        const resources = await getMyAuthorizedResources(
          BizAppConfig.appPermissionAppId || BizAppConfig.appSsoClientId || '',
        );
        if (active && currentRevision === revision)
          await apply(buildAuthorizedResourceAccessMap(resources));
      } catch {
        if (active && currentRevision === revision) await apply({});
      }
    };
    setInitialState((prev) => ({ ...prev, authorizedResourceAccess: {} }));
    void refresh();
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      window.removeEventListener('focus', refresh);
    };
  }, [currentTenantUuid, setInitialState]);
  return null;
}
